import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import useAuth from '../../hooks/use-auth';
import AuthModal from '../auth-modal';

const Container = styled.nav`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(10px);
    z-index: 1000;

    ul {
        display: flex;
        flex-direction: row;
        list-style: none;
        margin: 0;
        padding: 0;
        gap: 20px;
        font-family: 'Outfit', sans-serif;
        font-size: 0.8rem;
        font-weight: 500;
        color: #051923;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-right: 20px;
        align-items: center;
        justify-content: center;
        position: relative;
        z-index: 1000;
        transition: color 0.3s ease;
        li {
            cursor: pointer;
            transition: color 0.3s ease;
            &:hover {
                color: #161616;
            }
        }
    }

    @media (max-width: 1080px) {
        ul {
            display: none;
        }
    }
`;

const AvatarWrapper = styled.div`
    position: absolute;
    right: 35px;
    top: 35px;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const AvatarButton = styled.button`
    width: 70px;
    height: 70px;
    border-radius: 50%;
    border: 2px solid rgba(0, 0, 0, 0.15);
    background: ${(p) => (p.$loggedIn ? 'rgb(26, 148, 209)' : '#fff')};
    color: ${(p) => (p.$loggedIn ? '#fff' : '#6b7c8d')};
    font-family: 'Outfit', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s, box-shadow 0.2s;
    flex-shrink: 0;
    overflow: hidden;

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
`;

const DemoTooltip = styled.span`-    
    width: 220px;
    padding: 12px 14px;
    background: linear-gradient(135deg, #1a2332 0%, #2d3a4d 100%);
    color: #f0f4f8;
    font-family: 'Outfit', sans-serif;
    font-size: 0.8rem;
    font-weight: 500;
    line-height: 1.45;
    letter-spacing: 0.02em;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
    opacity: 0;
    transform: translate(6px, -50%);
    pointer-events: none;
    transition: opacity 0.25s ease, transform 0.25s ease;
    position: absolute;
    right: calc(100% + 14px);
    top: 150%;
    white-space: normal;
    text-align: right;
    top: 50%;

    &::after {
        content: '';
        position: absolute;
        right: -6px;
        top: 50%;
        margin-top: -6px;
        border-width: 6px 0 6px 6px;
        border-style: solid;
        border-color: transparent transparent transparent #2d3a4d;
        filter: drop-shadow(2px 0 2px rgba(0, 0, 0, 0.15));
    }

    ${AvatarWrapper}:hover & {
        opacity: 1;
        transform: translate(0, -50%);
    }
`;

const MACARONS_AVATAR_SRC = '/assets/avatar-macarons-shop.png';

const Navbar = () => {
    const { user, updateFn, logout } = useAuth();
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [demoAvatarError, setDemoAvatarError] = useState(false);

    const initial = user?.email ? user.email.charAt(0).toUpperCase() : '?';

    return (
        <>
            <Container>
                <Link
                    to="/"
                    style={{
                        position: 'absolute',
                        left: '25px',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                    }}
                >
                    <img src="/assets/logo-synaptek.png" alt="Logo Synaptek" style={{ width: '40px', height: '40px' }} />
                    <span style={{ fontFamily: 'Coolvetica', fontSize: '1.2rem', fontWeight: 600, color: '#161616' }}>Synaptek</span>
                </Link>

                <ul>
                    <li>Configurer mon chatbot</li>
                    <li>Comment ça marche ?</li>
                    <li>Cas d'application</li>
                    <li>Contact</li>
                </ul>

                <AvatarWrapper>
                    {!user && (
                        <DemoTooltip>
                            Essayez le tableau de bord avec le compte démo Macarons Shop
                        </DemoTooltip>
                    )}
                    <AvatarButton
                        type="button"
                        onClick={() => setAuthModalOpen(true)}
                        $loggedIn={!!user}
                        aria-label={user ? 'Mon compte' : 'Connexion'}
                        title={!user ? 'Essayez le tableau de bord avec le compte démo Macarons Shop' : undefined}
                    >
                        {user ? (
                            initial
                        ) : demoAvatarError ? (
                            'M'
                        ) : (
                            <img
                                src={MACARONS_AVATAR_SRC}
                                alt="Compte démo Macarons Shop"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={() => setDemoAvatarError(true)}
                            />
                        )}
                    </AvatarButton>
                </AvatarWrapper>
            </Container>

            <AuthModal
                isOpen={authModalOpen}
                onClose={() => setAuthModalOpen(false)}
                user={user}
                onLogin={updateFn}
                onLogout={logout}
            />
        </>
    );
};

export default Navbar;